import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useBackendClient } from '@/shared/kai/contexts/BackendClientContext'

import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

export const termsAcceptanceQueryKey = (walletAddress?: string) =>
  ['terms-acceptance', walletAddress] as const

export const useTermsAcceptanceData = () => {
  const backendClient = useBackendClient()
  const queryClient = useQueryClient()
  const walletAddress = useCurrentWalletAddress()

  const termsRes = useQuery({
    queryKey: termsAcceptanceQueryKey(walletAddress),
    queryFn: () => backendClient.getTermsAcceptance(walletAddress!),
    enabled: !!walletAddress,
    staleTime: Infinity,
  })

  const acceptMutation = useMutation({
    mutationFn: (address: string) => backendClient.acceptTerms(address),
    onSuccess: (_res, address) => {
      queryClient.setQueryData(termsAcceptanceQueryKey(address), { hasAccepted: true })
    },
    onError: () => {
      toast.error('Failed to accept terms. Please try again.')
    },
  })

  const acceptTerms = useCallback(() => {
    if (!walletAddress) {
      return
    }
    acceptMutation.mutate(walletAddress)
  }, [acceptMutation, walletAddress])

  return {
    hasAcceptedTerms: !!termsRes.data?.hasAccepted,
    isLoading: termsRes.isLoading,
    acceptTerms,
    isAcceptingTerms: acceptMutation.isPending,
  }
}
